import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { storage } from "@/lib/storage";
import { api } from "@/lib/api";
import { toast } from "sonner";

const PlayerContext = createContext(null);
export const usePlayer = () => useContext(PlayerContext);

const EQ_FREQS = [60, 250, 1000, 4000, 12000];
const EQ_KEYS = ["bass", "low", "mid", "high", "treble"];

export const EQ_PRESETS = {
  flat:    { bass: 0, low: 0, mid: 0, high: 0, treble: 0 },
  bass:    { bass: 8, low: 5, mid: 0, high: -2, treble: -3 },
  vocal:   { bass: -2, low: 0, mid: 5, high: 3, treble: 1 },
  club:    { bass: 6, low: 4, mid: -1, high: 3, treble: 5 },
  radio:   { bass: -3, low: 2, mid: 4, high: 2, treble: -1 },
  treble:  { bass: -2, low: -1, mid: 1, high: 5, treble: 8 },
};

const RECONNECT_MAX_ATTEMPTS = 4;
const RECONNECT_BASE_MS = 1200;
const NOW_PLAYING_POLL_MS = 25000;

export function PlayerProvider({ children }) {
  const audioRef = useRef(null);
  const audioCtxRef = useRef(null);
  const sourceRef = useRef(null);
  const analyserRef = useRef(null);
  const eqFiltersRef = useRef([]);
  const gainRef = useRef(null);
  const dataArrayRef = useRef(null);

  const destNodeRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const recChunksRef = useRef([]);

  const reconnectTimerRef = useRef(null);
  const reconnectAttemptsRef = useRef(0);
  const playTokenRef = useRef(0); // increments each playStation() to abort stale fades

  const nowPlayingTimerRef = useRef(null);
  const sleepTickRef = useRef(null);
  const sleepFadeRef = useRef(null);

  const [current, setCurrent] = useState(null);
  const [playing, setPlaying] = useState(false);
  const [loading, setLoading] = useState(false);
  const [volume, setVolume] = useState(storage.getVolume());
  const [muted, setMuted] = useState(false);
  const [favorites, setFavorites] = useState(storage.getFavorites());
  const [history, setHistory] = useState(storage.getHistory());
  const [eq, setEq] = useState(storage.getEQ());
  const [recording, setRecording] = useState(false);
  const [recordSeconds, setRecordSeconds] = useState(0);
  const [nowPlaying, setNowPlaying] = useState(""); // song title from ICY
  const [sleepTimerMinutes, setSleepTimerMinutes] = useState(0);
  const [sleepRemaining, setSleepRemaining] = useState(0);
  const [alarm, setAlarmState] = useState(storage.getAlarm());
  const [stats, setStats] = useState(storage.getStats());
  const [hours, setHours] = useState(storage.getHours());
  const [moodStats, setMoodStats] = useState(storage.getMoodStats());
  // Track consecutive stream failures for the same mood so we can auto-suggest
  const failureCountRef = useRef(0);
  const listenAccumRef = useRef(0);
  const listenTickerRef = useRef(null);

  useEffect(() => {
    const audio = new Audio();
    audio.crossOrigin = "anonymous";
    audio.preload = "none";
    audio.volume = storage.getVolume();
    audioRef.current = audio;

    const onPlay = () => {
      setPlaying(true);
      setLoading(false);
      reconnectAttemptsRef.current = 0;
      if (listenTickerRef.current) clearInterval(listenTickerRef.current);
      listenTickerRef.current = setInterval(() => {
        listenAccumRef.current += 1;
        if (listenAccumRef.current >= 15) {
          const flush = listenAccumRef.current;
          listenAccumRef.current = 0;
          const updated = storage.addSeconds(flush);
          if (updated) setHours({ ...updated });
        }
      }, 1000);
    };
    const onPause = () => {
      setPlaying(false);
      if (listenTickerRef.current) { clearInterval(listenTickerRef.current); listenTickerRef.current = null; }
      if (listenAccumRef.current > 0) {
        const flush = listenAccumRef.current;
        listenAccumRef.current = 0;
        const updated = storage.addSeconds(flush);
        if (updated) setHours({ ...updated });
      }
    };
    const onWaiting = () => setLoading(true);
    const onPlaying = () => setLoading(false);
    const onError = () => tryReconnect();
    const onStalled = () => tryReconnect();
    const onEnded = () => tryReconnect();

    audio.addEventListener("play", onPlay);
    audio.addEventListener("pause", onPause);
    audio.addEventListener("waiting", onWaiting);
    audio.addEventListener("playing", onPlaying);
    audio.addEventListener("error", onError);
    audio.addEventListener("stalled", onStalled);
    audio.addEventListener("ended", onEnded);
    return () => {
      audio.pause();
      audio.removeEventListener("play", onPlay);
      audio.removeEventListener("pause", onPause);
      audio.removeEventListener("waiting", onWaiting);
      audio.removeEventListener("playing", onPlaying);
      audio.removeEventListener("error", onError);
      audio.removeEventListener("stalled", onStalled);
      audio.removeEventListener("ended", onEnded);
    };
  }, []);

  const tryReconnect = useCallback(() => {
    const audio = audioRef.current;
    if (!audio || !audio.src) return;
    if (reconnectAttemptsRef.current >= RECONNECT_MAX_ATTEMPTS) {
      setLoading(false);
      setPlaying(false);
      toast.error("Stream disconnected. Try another station.");
      reconnectAttemptsRef.current = 0;
      return;
    }
    const attempt = ++reconnectAttemptsRef.current;
    const delay = RECONNECT_BASE_MS * attempt;
    if (reconnectTimerRef.current) clearTimeout(reconnectTimerRef.current);
    reconnectTimerRef.current = setTimeout(async () => {
      try {
        setLoading(true);
        const src = audio.src;
        audio.src = "";
        audio.src = src;
        await audio.play();
      } catch (err) {
        console.warn("[voix] reconnect failed:", err);
      }
    }, delay);
  }, []);

  useEffect(() => {
    if (!recording) return;
    const t = setInterval(() => setRecordSeconds((s) => s + 1), 1000);
    return () => clearInterval(t);
  }, [recording]);

  const ensureAudioGraph = useCallback(() => {
    if (audioCtxRef.current) return;
    try {
      const Ctx = window.AudioContext || window.webkitAudioContext;
      const ctx = new Ctx();
      const source = ctx.createMediaElementSource(audioRef.current);
      const gain = ctx.createGain();
      const analyser = ctx.createAnalyser();
      analyser.fftSize = 128;
      analyser.smoothingTimeConstant = 0.75;
      const filters = EQ_FREQS.map((f, i) => {
        const filter = ctx.createBiquadFilter();
        filter.type = i === 0 ? "lowshelf" : i === EQ_FREQS.length - 1 ? "highshelf" : "peaking";
        filter.frequency.value = f;
        filter.Q.value = 1;
        filter.gain.value = eq[EQ_KEYS[i]] || 0;
        return filter;
      });
      let node = source;
      filters.forEach((f) => { node.connect(f); node = f; });
      node.connect(gain);
      gain.connect(analyser);
      analyser.connect(ctx.destination);

      const dest = ctx.createMediaStreamDestination();
      analyser.connect(dest);

      audioCtxRef.current = ctx;
      sourceRef.current = source;
      eqFiltersRef.current = filters;
      gainRef.current = gain;
      analyserRef.current = analyser;
      destNodeRef.current = dest;
      dataArrayRef.current = new Uint8Array(analyser.frequencyBinCount);
    } catch (e) {
      console.warn("[voix] audio graph error:", e);
    }
  }, [eq]);

  const applyEQ = useCallback((newEq) => {
    setEq(newEq);
    storage.setEQ(newEq);
    if (eqFiltersRef.current.length) {
      eqFiltersRef.current.forEach((f, i) => {
        f.gain.value = newEq[EQ_KEYS[i]] || 0;
      });
    }
  }, []);

  const applyPreset = useCallback((name) => {
    const preset = EQ_PRESETS[name] || EQ_PRESETS.flat;
    applyEQ({ ...preset, preset: name });
  }, [applyEQ]);

  // Media Session API for OS-level controls
  const updateMediaSession = useCallback((station, songTitle) => {
    if (!("mediaSession" in navigator) || !station) return;
    try {
      navigator.mediaSession.metadata = new window.MediaMetadata({
        title: songTitle || station.name,
        artist: songTitle ? station.name : station.country,
        album: "Voix · World Radio",
        artwork: station.favicon
          ? [96, 128, 192, 256, 384, 512].map((s) => ({ src: station.favicon, sizes: `${s}x${s}`, type: "image/png" }))
          : [],
      });
    } catch (err) {
      console.warn("[voix] mediaSession failed:", err);
    }
  }, []);

  const fetchNowPlaying = useCallback(async (station) => {
    if (!station || !station.url) return;
    try {
      const data = await api.nowPlaying(station.url);
      const title = (data && data.title) || "";
      setNowPlaying(title);
      updateMediaSession(station, title);
    } catch (err) {
      /* silent — not all streams expose ICY */
    }
  }, [updateMediaSession]);

  const playStation = useCallback(async (station) => {
    if (!station || !station.url) {
      toast.error("Station has no stream URL");
      return;
    }
    const audio = audioRef.current;
    reconnectAttemptsRef.current = 0;
    if (reconnectTimerRef.current) clearTimeout(reconnectTimerRef.current);
    setNowPlaying("");

    // Bump a token so any in-flight fade from a previous playStation() bails out.
    const myToken = ++playTokenRef.current;

    // Crossfade: if we were already playing, fade out over ~350ms before swapping.
    const wasPlaying = !audio.paused && audio.src && audio.currentTime > 0;
    const targetVolume = audio.volume;
    if (wasPlaying) {
      const steps = 7;
      for (let i = 0; i < steps; i++) {
        if (playTokenRef.current !== myToken) return; // superseded
        audio.volume = Math.max(0, targetVolume * (1 - (i + 1) / steps));
        await new Promise((r) => setTimeout(r, 50));
      }
    }
    if (playTokenRef.current !== myToken) return;

    setCurrent(station);
    setLoading(true);
    try {
      audio.src = station.url;
      audio.volume = wasPlaying ? 0 : targetVolume;
      ensureAudioGraph();
      if (audioCtxRef.current && audioCtxRef.current.state === "suspended") {
        await audioCtxRef.current.resume();
      }
      await audio.play();
      if (playTokenRef.current !== myToken) return;

      if (wasPlaying) {
        const steps = 8;
        for (let i = 0; i < steps; i++) {
          if (playTokenRef.current !== myToken) return;
          audio.volume = Math.min(targetVolume, (targetVolume * (i + 1)) / steps);
          await new Promise((r) => setTimeout(r, 60));
        }
      } else {
        audio.volume = targetVolume;
      }

      const newHist = storage.pushHistory(station);
      setHistory(newHist);
      const newStats = storage.bumpCountry(station.country);
      if (newStats) setStats({ ...newStats });
      if (station.mood) {
        const newMood = storage.bumpMood(station.mood);
        if (newMood) setMoodStats({ ...newMood });
      }
      failureCountRef.current = 0;
      api.registerClick(station.stationuuid);
      updateMediaSession(station, "");
      fetchNowPlaying(station);
      if (nowPlayingTimerRef.current) clearInterval(nowPlayingTimerRef.current);
      nowPlayingTimerRef.current = setInterval(() => fetchNowPlaying(station), NOW_PLAYING_POLL_MS);
    } catch (e) {
      console.warn("[voix] play failed:", e);
      audio.volume = targetVolume;
      setLoading(false);
      setPlaying(false);
      // Auto-suggest a same-mood station after 3 consecutive failures
      failureCountRef.current += 1;
      if (failureCountRef.current >= 3 && station.mood) {
        failureCountRef.current = 0;
        toast("Trying another station in the same mood…");
        try {
          const mood = station.mood;
          const alt = await api.randomStation({ tag: station.tag || undefined });
          if (alt && alt.stationuuid !== station.stationuuid) {
            playStation({ ...alt, mood });
            return;
          }
        } catch (err2) {
          console.warn("[voix] auto-suggest failed:", err2);
        }
      }
      toast.error("Cannot start stream. Trying to load…");
    }
  }, [ensureAudioGraph, fetchNowPlaying, updateMediaSession]);

  const togglePlay = useCallback(async () => {
    const audio = audioRef.current;
    if (!current) return;
    if (audio.paused) {
      setLoading(true);
      try { await audio.play(); } catch (err) { console.warn("[voix] play failed:", err); setLoading(false); }
    } else {
      audio.pause();
    }
  }, [current]);

  const stop = useCallback(() => {
    const audio = audioRef.current;
    audio.pause();
    audio.src = "";
    setCurrent(null);
    setPlaying(false);
    setNowPlaying("");
    if (nowPlayingTimerRef.current) clearInterval(nowPlayingTimerRef.current);
  }, []);

  const changeVolume = useCallback((v) => {
    setVolume(v);
    audioRef.current.volume = v;
    storage.setVolume(v);
    if (v > 0 && muted) {
      setMuted(false);
      audioRef.current.muted = false;
    }
  }, [muted]);

  const toggleMute = useCallback(() => {
    const next = !muted;
    setMuted(next);
    audioRef.current.muted = next;
  }, [muted]);

  const toggleFavorite = useCallback((station) => {
    const list = storage.toggleFavorite(station);
    setFavorites([...list]);
    const isFav = list.some((s) => s.stationuuid === station.stationuuid);
    toast(isFav ? "Added to favorites" : "Removed from favorites");
  }, []);

  const startRecording = useCallback(() => {
    if (!current) {
      toast.error("Play a station first");
      return;
    }
    ensureAudioGraph();
    if (!destNodeRef.current) {
      toast.error("Recording unsupported in this browser");
      return;
    }
    try {
      const mime = MediaRecorder.isTypeSupported("audio/webm;codecs=opus")
        ? "audio/webm;codecs=opus"
        : "audio/webm";
      const rec = new MediaRecorder(destNodeRef.current.stream, { mimeType: mime });
      recChunksRef.current = [];
      rec.ondataavailable = (e) => { if (e.data.size) recChunksRef.current.push(e.data); };
      rec.onstop = () => {
        const blob = new Blob(recChunksRef.current, { type: mime });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        const safeName = (current?.name || "voix-mix").replace(/[^a-z0-9-_]/gi, "_").slice(0, 40);
        a.href = url;
        a.download = `voix_${safeName}_${new Date().toISOString().slice(0,19).replace(/[:T]/g,"-")}.webm`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        setTimeout(() => URL.revokeObjectURL(url), 3000);
        toast.success("Recording saved");
      };
      rec.start();
      mediaRecorderRef.current = rec;
      setRecording(true);
      setRecordSeconds(0);
      toast("Recording started");
    } catch (e) {
      console.warn("[voix] recording start failed:", e);
      toast.error("Recording failed to start");
    }
  }, [current, ensureAudioGraph]);

  const stopRecording = useCallback(() => {
    const rec = mediaRecorderRef.current;
    if (rec && rec.state !== "inactive") rec.stop();
    setRecording(false);
    setRecordSeconds(0);
  }, []);

  const shareStation = useCallback(async (station) => {
    const s = station || current;
    if (!s) return;
    const url = `${window.location.origin}/?station=${encodeURIComponent(s.stationuuid)}`;
    try {
      if (navigator.share) {
        await navigator.share({ title: `Voix · ${s.name}`, text: `Listen to ${s.name}${s.country ? " from " + s.country : ""} on Voix`, url });
      } else {
        await navigator.clipboard.writeText(url);
        toast.success("Share link copied");
      }
    } catch (err) {
      if (err && err.name !== "AbortError") console.warn("[voix] share failed:", err);
    }
  }, [current]);

  const clearHistory = useCallback(() => {
    storage.clearHistory();
    setHistory([]);
  }, []);

  // Sleep timer
  const cancelSleepTimer = useCallback(() => {
    if (sleepTickRef.current) clearInterval(sleepTickRef.current);
    if (sleepFadeRef.current) clearInterval(sleepFadeRef.current);
    sleepTickRef.current = null;
    sleepFadeRef.current = null;
    setSleepTimerMinutes(0);
    setSleepRemaining(0);
  }, []);

  const startSleepTimer = useCallback((minutes) => {
    cancelSleepTimer();
    setSleepTimerMinutes(minutes);
    const totalSecs = minutes * 60;
    setSleepRemaining(totalSecs);
    const audio = audioRef.current;
    const startedVolume = audio.volume;
    sleepTickRef.current = setInterval(() => {
      setSleepRemaining((s) => {
        if (s <= 1) {
          clearInterval(sleepTickRef.current);
          sleepTickRef.current = null;
          // Fade over 6 seconds
          let step = 0;
          const initialVol = audio.volume || startedVolume;
          sleepFadeRef.current = setInterval(() => {
            step++;
            const v = Math.max(0, initialVol * (1 - step / 20));
            audio.volume = v;
            if (step >= 20) {
              clearInterval(sleepFadeRef.current);
              sleepFadeRef.current = null;
              audio.pause();
              audio.volume = startedVolume; // restore
              setSleepTimerMinutes(0);
              setSleepRemaining(0);
              toast("Sleep timer ended · goodnight");
            }
          }, 300);
          return 0;
        }
        return s - 1;
      });
    }, 1000);
    toast(`Sleep timer set for ${minutes} min`);
  }, [cancelSleepTimer]);

  // Media Session action handlers
  useEffect(() => {
    if (!("mediaSession" in navigator)) return;
    try {
      navigator.mediaSession.setActionHandler("play", () => togglePlay());
      navigator.mediaSession.setActionHandler("pause", () => togglePlay());
      navigator.mediaSession.setActionHandler("stop", () => stop());
    } catch (err) {
      /* not all browsers support all actions */
    }
  }, [togglePlay, stop]);

  // ---------- Alarm clock ----------
  const setAlarm = useCallback((next) => {
    const alarmData = next && next.enabled ? next : null;
    storage.setAlarm(alarmData);
    setAlarmState(alarmData);
    if (alarmData) toast(`Alarm set for ${alarmData.time}`);
  }, []);

  const cancelAlarm = useCallback(() => {
    storage.clearAlarm();
    setAlarmState(null);
    toast("Alarm cancelled");
  }, []);

  // Poll each 20s to check whether the alarm should fire
  useEffect(() => {
    const check = () => {
      const a = storage.getAlarm();
      if (!a || !a.enabled) return;
      const now = new Date();
      const hh = String(now.getHours()).padStart(2, "0");
      const mm = String(now.getMinutes()).padStart(2, "0");
      const currentMinute = `${hh}:${mm}`;
      const stamp = `${now.toDateString()} ${currentMinute}`;
      if (currentMinute === a.time && a.lastFiredAtMinute !== stamp) {
        const fired = { ...a, lastFiredAtMinute: stamp };
        storage.setAlarm(fired);
        setAlarmState(fired);
        if (a.station) {
          toast("⏰ Alarm — waking with radio");
          playStation(a.station);
        }
      }
    };
    check();
    const t = setInterval(check, 20000);
    return () => clearInterval(t);
  }, [playStation]);

  const value = useMemo(() => ({
    current, playing, loading,
    volume, muted, favorites, history, eq, recording, recordSeconds,
    nowPlaying,
    sleepTimerMinutes, sleepRemaining,
    alarm, setAlarm, cancelAlarm,
    stats, hours, moodStats,
    playStation, togglePlay, stop,
    changeVolume, toggleMute,
    toggleFavorite, isFavorite: (uuid) => favorites.some((s) => s.stationuuid === uuid),
    startRecording, stopRecording,
    shareStation, clearHistory,
    applyEQ, applyPreset,
    startSleepTimer, cancelSleepTimer,
    getAnalyser: () => analyserRef.current,
    getDataArray: () => dataArrayRef.current,
  }), [current, playing, loading, volume, muted, favorites, history, eq, recording, recordSeconds,
      nowPlaying, sleepTimerMinutes, sleepRemaining, alarm, stats, hours, moodStats,
      playStation, togglePlay, stop, changeVolume, toggleMute, toggleFavorite,
      startRecording, stopRecording, shareStation, clearHistory, applyEQ, applyPreset,
      startSleepTimer, cancelSleepTimer, setAlarm, cancelAlarm]);

  return <PlayerContext.Provider value={value}>{children}</PlayerContext.Provider>;
}
